import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { drawFinale, DISPLAY_FONT, LOGO_SIZE } from './drawFinale';
import {
    EXIT, PLAIN_EXIT, PLANS, PRELOAD_CAP_MS, SAFETY_CAP_MS, choosePlan, firstNameFrom, frameAt, normalizeSetting,
} from '../../lib/welcomeSequence';
import './signin.css';

const defaultClock = {
    now: () => performance.now(),
    raf: (fn) => window.requestAnimationFrame(fn),
    caf: (id) => window.cancelAnimationFrame(id),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (id) => window.clearTimeout(id),
};

function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
}

function withCap(promise, ms, clock) {
    return new Promise((resolve) => {
        const timer = clock.setTimeout(() => resolve(null), Math.max(0, ms));
        Promise.resolve(promise).then((value) => { clock.clearTimeout(timer); resolve(value); }, () => { clock.clearTimeout(timer); resolve(null); });
    });
}

function loadImage(url) {
    if (!url) return { image: null, ready: Promise.resolve(null) };
    const image = new Image();
    const ready = new Promise((resolve) => {
        image.onload = () => resolve(image);
        image.onerror = () => resolve(null);
    });
    image.src = url;
    return { image, ready };
}

function loadFont() {
    try { return document.fonts?.load(`200 100px ${DISPLAY_FONT}`) || Promise.resolve(); } catch (_) { return Promise.resolve(); }
}

/**
 * BL-402 welcome sequence. Plays the film finale over the Command Center while it loads underneath.
 * Leaves ONLY when (a) the logo is finished and (b) `ready` is true. `aborted` (sign-in didn't land in the
 * app: wrong tenant, verification step, error) leaves at once. Skip jumps to the finished logo, never past
 * the ready check.
 */
export default function WelcomeSequence({
    ready,
    aborted = false,
    loadContext,
    user = null,
    initialBrand = null,
    onContext,
    onExitStart,
    onDone,
    clock = defaultClock,
    storage,
    reducedMotion,
}) {
    const [phase, setPhase] = useState('preparing'); // preparing | canvas | plain
    const [exiting, setExiting] = useState(false);
    const [exitSeconds, setExitSeconds] = useState(EXIT);
    const canvasRef = useRef(null);
    const skipRef = useRef(null);
    const readyRef = useRef(ready);
    const abortedRef = useRef(aborted);
    const st = useRef(null);
    const finished = useRef(false);
    const callbacks = useRef({ onExitStart, onDone, onContext });
    callbacks.current = { onExitStart, onDone, onContext };
    const [safetyReady, setSafetyReady] = useState(false);
    readyRef.current = ready || safetyReady;
    abortedRef.current = aborted;

    const startExit = (seconds) => {
        if (finished.current) return;
        finished.current = true;
        setExitSeconds(seconds);
        setExiting(true);
        callbacks.current.onExitStart?.();
        clock.setTimeout(() => callbacks.current.onDone?.(), seconds * 1000);
    };

    // Never trap anyone behind the overlay if the app stalls.
    useEffect(() => {
        const timer = clock.setTimeout(() => setSafetyReady(true), SAFETY_CAP_MS);
        return () => clock.clearTimeout(timer);
    }, [clock]);

    // Preload font + logo + who/which brand, capped at 600ms, then choose the plan and start.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const deadline = clock.now() + PRELOAD_CAP_MS;
            const context = await withCap(loadContext ? loadContext() : null, PRELOAD_CAP_MS, clock);
            if (cancelled) return;
            if (context) callbacks.current.onContext?.(context);
            const brand = context?.brand_name ? context : (initialBrand || {});
            const setting = normalizeSetting(context?.signin_sequence || initialBrand?.signin_sequence);
            const reduced = reducedMotion ?? prefersReducedMotion();
            const planName = choosePlan({ setting, reducedMotion: reduced, storage: storage || safeLocalStorage() });
            if (!planName) { setPhase('plain'); return; }
            const logo = loadImage(brand.logo_url);
            await withCap(Promise.all([logo.ready, loadFont()]), deadline - clock.now(), clock);
            if (cancelled) return;
            const ctx = canvasContext();
            if (!ctx) { setPhase('plain'); return; }
            const plan = PLANS[planName];
            st.current = {
                plan: planName, t0: clock.now(), skipped: false, skippedAt: 0, exitAt: 0, aborted: false,
                name: plan.name ? firstNameFrom(context, user) : null,
                logo: logo.image, brandName: brand.brand_name || '', sheen: plan.sheen, ...ctx,
            };
            setPhase('canvas');
        })();
        return () => { cancelled = true; };
        // Runs once per sequence: later prop changes flow through refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Plain fade: reduced motion, the tenant chose Off, or no canvas available. Leaves the moment the app is ready.
    useEffect(() => {
        if (phase === 'plain' && (ready || safetyReady || aborted)) startExit(PLAIN_EXIT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, ready, safetyReady, aborted]);

    // Canvas loop.
    useEffect(() => {
        if (phase !== 'canvas') return undefined;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) { setPhase('plain'); return undefined; }
        skipRef.current?.focus({ preventScroll: true });
        const size = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const rect = canvas.getBoundingClientRect();
            canvas.width = Math.max(1, Math.round((rect.width || window.innerWidth) * dpr));
            canvas.height = Math.max(1, Math.round((rect.height || window.innerHeight) * dpr));
        };
        size();
        window.addEventListener('resize', size);
        let frame = 0;
        const tick = () => {
            const s = st.current;
            const now = clock.now();
            s.aborted = abortedRef.current;
            const f = frameAt(s, now, readyRef.current);
            if (f.exitAt && !s.exitAt) { s.exitAt = f.exitAt; startExit(EXIT); }
            const logo = s.logo && s.logo.complete && s.logo.naturalWidth > 0 ? s.logo : null;
            drawFinale(ctx, {
                W: canvas.width, H: canvas.height, t: f.filmT, name: s.name, logo, brandName: s.brandName,
                lg: s.lg, lgC: s.lgC, hold: f.hold, out: f.out, sheen: s.sheen,
            });
            if (!f.done) frame = clock.raf(tick);
        };
        frame = clock.raf(tick);
        return () => { clock.caf(frame); window.removeEventListener('resize', size); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // Skip: click/tap anywhere, Esc, or the Skip pill. Jumps to the finished logo; still waits for ready.
    const skip = () => {
        const s = st.current;
        if (s && !s.skipped) { s.skipped = true; s.skippedAt = clock.now(); }
    };
    useEffect(() => {
        const onKey = (event) => { if (event.key === 'Escape') skip(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const plain = phase !== 'canvas';
    return createPortal(
        <section
            className={`ia-welcome${plain ? ' ia-welcome--plain' : ''}${exiting ? ' is-exiting' : ''}`}
            style={{ '--ia-exit': `${exitSeconds}s` }}
            aria-label="Welcome animation. Click or press Escape to skip."
            data-testid="welcome-sequence"
            data-phase={phase}
            onClick={skip}
        >
            {phase === 'canvas' && <canvas ref={canvasRef} aria-hidden="true" />}
            {phase === 'canvas' && (
                <button ref={skipRef} type="button" className="ia-welcome__skip" onClick={(event) => { event.stopPropagation(); skip(); }}>Skip</button>
            )}
        </section>,
        document.body,
    );
}

function safeLocalStorage() {
    try { return window.localStorage; } catch (_) { return { getItem: () => { throw new Error('no storage'); }, setItem: () => {} }; }
}

// The offscreen square the logo and its sheen are composited on. Null when canvas isn't available.
function canvasContext() {
    try {
        const lgC = document.createElement('canvas');
        lgC.width = LOGO_SIZE; lgC.height = LOGO_SIZE;
        const lg = lgC.getContext('2d');
        return lg ? { lgC, lg } : null;
    } catch (_) {
        return null;
    }
}
